import {
    AirspaceType,
    FlightAirspaceCrossing,
    FlightLevel,
    FlightPointAssociatedRoute,
    FlightPointDto,
    LatLonAltDistPoint,
    LatLonPoint,
} from 'app/model/CORE';
import AirspaceUtils from 'app/common/util/AirspaceUtils';
import MathUtils from 'app/common/util/MathUtils';
import FlightLevelUtils from 'app/common/util/FlightLevelUtils';

/**
 * Utilities for manipulating flight profiles.
 * Contains functionality found in the GWT FlightPointDto, FlightAirspaceCrossing
 * Inspired by FlightPointDto and CommonFlightUtils GWT s/w
 */
export default class FlightProfileUtils {
    /**
     * Vector points have only time/FL values, with no geographical (lat/lon) point.
     * Such points typically denote important climb points (e.g. BOC / TOC / BOD / TOD).
     */
    static isVectorPoint(fp: FlightPointDto) {
        return !fp.point && !fp.aerodrome;
    }

    /**
     * Return true if this flight point has a lat/lon position.
     */
    static hasPosition(fp: FlightPointDto) {
        return fp.point?.position;
    }

    /**
     * Return true if this flight point has a named reference location.
     * (either an aerodrome or a named point).
     * @return
     */
    static hasName(fp: FlightPointDto) {
        return fp.aerodrome || fp.point?.pointId;
    }

    /**
     * Return true if has a known point.
     * Point IDs containing asterisks are not considered as known, since they are NM-defined points.
     * @return
     */
    static hasKnownPoint(fp: FlightPointDto) {
        return fp.point?.pointId && !fp.point?.pointId.includes('*');
    }

    /**
     * Return the name of the flight point's reference location.
     * (either an aerodrome or point ID).
     * @return
     */
    static getName(fp: FlightPointDto) {
        if (fp.aerodrome) return fp.aerodrome;
        else if (fp.point) return fp.point.pointId;
        else return null;
    }

    static getDisplayString(fp: FlightPointDto) {
        if (fp.aerodrome) return fp.aerodrome;
        if (fp.point) return AirspaceUtils.toIcaoPointString(fp.point);
        return null;
    }

    static getNamedPoint(points: FlightPointDto[], ptId: string): FlightPointDto {
        const idx = FlightProfileUtils.getPointIndex(points, ptId);
        return idx === -1 ? null : points[idx];
    }

    static getTimeOverPoint(points: FlightPointDto[], ptId: string): Date {
        const idx = FlightProfileUtils.getPointIndex(points, ptId);
        return idx === -1 ? null : points[idx].timeOver;
    }

    static getPointIndex(points: FlightPointDto[], ptId: string): number {
        if (!points) return -1;
        for (let i = 0; i < points.length; i++) {
            if (ptId === FlightProfileUtils.getName(points[i])) return i;
        }
        return -1;
    }

    static getPointIdxBeforeOrAt(points: FlightPointDto[], time: Date, initialIdx: number = 0) {
        if (!points) return -1;
        for (let i = initialIdx; i < points.length; i++) {
            const diff = time.getTime() - points[i].timeOver.getTime();
            if (diff === 0) return i;
            if (diff < 0) return i - 1;
        }
        // return -1; // original GWT code had this.. but I believe its incorrect
        return points.length - 1;
    }

    static getPointIdxAfterOrAt(points: FlightPointDto[], time: Date, initialIdx: number = 0) {
        if (!points) return -1;
        for (let i = initialIdx; i < points.length; i++) {
            if (points[i].timeOver.getTime() >= time.getTime()) return i;
        }
        return -1;
    }

    static getNonVectorPointAfter(points: Array<FlightPointDto>, idx: number): number {
        for (let i = idx + 1; i < points.length; i++) {
            if (!FlightProfileUtils.isVectorPoint(points[i])) return i;
        }
        return -1;
    }

    static getNonVectorPointBefore(points: Array<FlightPointDto>, idx: number): number {
        for (let i = idx - 1; i >= 0; i--) {
            if (!FlightProfileUtils.isVectorPoint(points[i])) return i;
        }
        return -1;
    }

    /**
     * Interpolate the flight level at a given time.
     * @param points
     * @param time
     * @param initialIdx
     */
    static interpolateLevelAtTime(points: Array<FlightPointDto>, time: Date, initialIdx: number = 0): FlightLevel {
        let reply: FlightLevel = null;
        const idx = FlightProfileUtils.getPointIdxBeforeOrAt(points, time, initialIdx);

        if (idx !== -1) {
            const pt1 = points[idx];
            if (pt1.timeOver.getTime() === time.getTime()) {
                reply = pt1.flightLevel;
            } else {
                const pt2 = points[idx + 1];
                const percentage = MathUtils.calcPercentageAlong(
                    pt1.timeOver.getTime(),
                    pt2.timeOver.getTime(),
                    time.getTime(),
                );
                reply = FlightLevelUtils.newFlightLevel(
                    Math.floor(
                        MathUtils.interpolate(
                            FlightLevelUtils.getLevelValue(pt1.flightLevel),
                            FlightLevelUtils.getLevelValue(pt2.flightLevel),
                            percentage,
                        ),
                    ),
                );
            }
        }
        return reply;
    }

    /**
     * Interpolate a 3D position along the trajectory for the give time.
     * This method simply interpolates directly over the (lat,lon,alt, distance along) values.
     * No spherical computations are performed.
     * @param points
     * @param time
     * @param initialIdx
     * @return
     */
    static interpolateAtTime(points: Array<FlightPointDto>, time: Date, initialIdx: number = 0): LatLonAltDistPoint {
        let reply: LatLonAltDistPoint = null;
        const idx = FlightProfileUtils.getPointIdxBeforeOrAt(points, time, initialIdx);

        if (idx !== -1) {
            const pt1 = points[idx];
            const startPt = FlightProfileUtils.isVectorPoint(pt1)
                ? FlightProfileUtils.getVectorPointPosition(points, idx)
                : pt1.point.position;
            const startLevel = pt1.flightLevel;

            if (pt1.timeOver.getTime() === time.getTime()) {
                reply = {
                    latitude: startPt.latitude,
                    longitude: startPt.longitude,
                    level: startLevel,
                    distanceAlongInNm: pt1.coveredDistance,
                    pointBeforeOrAtIdx: idx,
                };
            } else {
                const pt2 = points[idx + 1];
                const endPt = FlightProfileUtils.isVectorPoint(pt2)
                    ? FlightProfileUtils.getVectorPointPosition(points, idx + 1)
                    : pt2.point.position;
                const endLevel = pt2.flightLevel;

                const percentage = MathUtils.calcPercentageAlong(
                    pt1.timeOver.getTime(),
                    pt2.timeOver.getTime(),
                    time.getTime(),
                );

                reply = {
                    latitude: MathUtils.interpolate(startPt.latitude, endPt.latitude, percentage),
                    longitude: MathUtils.interpolate(startPt.longitude, endPt.longitude, percentage),
                    level: FlightLevelUtils.newFlightLevel(
                        Math.floor(
                            MathUtils.interpolate(
                                FlightLevelUtils.getLevelValue(startLevel),
                                FlightLevelUtils.getLevelValue(endLevel),
                                percentage,
                            ),
                        ),
                    ),
                    distanceAlongInNm: MathUtils.interpolate(pt1.coveredDistance, pt2.coveredDistance, percentage),
                    pointBeforeOrAtIdx: idx,
                };
            }
        }
        return reply;
    }

    /**
     * The FlightPointDTO at index 'idx' is a vector point.
     * Compute the (lat/lon) position of such a point, based on interpolation between the previous and next non-vector points.
     *
     * @param points
     * @param idx
     * @return
     */
    static getVectorPointPosition(points: Array<FlightPointDto>, idx: number): LatLonPoint {
        const vecPt = points[idx];
        const beforePt = points[FlightProfileUtils.getNonVectorPointBefore(points, idx)];
        const afterPt = points[FlightProfileUtils.getNonVectorPointAfter(points, idx)];
        const percentage = MathUtils.calcPercentageAlong(
            beforePt.coveredDistance,
            afterPt.coveredDistance,
            vecPt.coveredDistance,
        );

        return {
            latitude: MathUtils.interpolate(
                beforePt.point.position.latitude,
                afterPt.point.position.latitude,
                percentage,
            ),
            longitude: MathUtils.interpolate(
                beforePt.point.position.longitude,
                afterPt.point.position.longitude,
                percentage,
            ),
        };
    }

    /**
     * returns a 2 element list covering the min/max duration in the ifpuz region.
     * @param crossings
     * @return
     */
    static getIfpuzPeriod(crossings: FlightAirspaceCrossing[]): [FlightAirspaceCrossing, FlightAirspaceCrossing] {
        let earliest = null;
        let latest = null;
        if (crossings) {
            for (const fac of crossings) {
                if (fac.airspaceType === AirspaceType.AUA && AirspaceUtils.isInIfpuz(fac.airspaceId)) {
                    if (!earliest || fac.firstEntryTime.getTime() < earliest.firstEntryTime.getTime()) {
                        earliest = fac;
                    }
                    if (!latest || fac.lastExitTime.getTime() > latest.lastExitTime.getTime()) {
                        latest = fac;
                    }
                }
            }
        }
        return [earliest, latest];
    }

    /**
     * Returns the first AUAU in the IFPUZ zone for this flight.
     * If the flight departs from within the IFPUZ then the AUA around the departure aerodrome is returned.
     * @param facList
     * @return
     */
    static getIfpuzEntry(facList: FlightAirspaceCrossing[]): FlightAirspaceCrossing {
        for (const fac of facList) {
            if (fac.airspaceType === AirspaceType.AUA && AirspaceUtils.isInIfpuz(fac.airspaceId)) {
                return fac;
            }
        }
        return null;
    }

    /**
     * Returns the id of a FlightPointAssociatedRoute.
     * If is a direct returns DCT
     * If is a star, returns the star id
     * If is a sid, returns the sid id
     * Otherwise returns routeId
     * @param route
     */
    static getFlightPointAssociatedRouteId(route: FlightPointAssociatedRoute) {
        if (!route) return null;
        if (route.direct) return 'DCT';
        if (route.star) return route.star.id;
        if (route.sid != null) return route.sid.id;
        return route.routeId;
    }

    /**
     * The FlightPointDTO at index 'idx' is a vector point.
     * Compute the percentage of such a point, based on interpolation between the previous and next non-vector points.
     *
     * @param points
     * @param idx
     * @return
     */
    static getVectorPointPercentage(points: FlightPointDto[], idx: number): number {
        if (!points) return null;
        const vecPt: FlightPointDto = points[idx];
        const beforePt: FlightPointDto = points[this.getNonVectorPointBefore(points, idx)];
        const afterPt: FlightPointDto = points[this.getNonVectorPointAfter(points, idx)];

        if (vecPt && beforePt && afterPt)
            return MathUtils.calcPercentageAlong(
                beforePt.coveredDistance,
                afterPt.coveredDistance,
                vecPt.coveredDistance,
            );
        return null;
    }

    static getFlightPointDisplayString(points: Array<FlightPointDto>, index: number): string {
        if (points && index) {
            const point = points[index];
            if (FlightProfileUtils.isVectorPoint(point)) {
                return Math.trunc(FlightProfileUtils.getVectorPointPercentage(points, index) * 100) + '%';
            } else if (point.aerodrome) {
                return point.aerodrome;
            } else if (point.point) {
                return AirspaceUtils.toIcaoPointString(point.point);
            }
        }
    }
}
